import React, {useContext} from 'react';
import glamorous from 'glamorous';
import PropTypes from 'prop-types';
import {CHARCOAL, FOCUS_BLUE, WHITE, BLACK, TARMAC} from '../../../../shared/style/colors';
import {PHONE} from '../../../../shared/style/breakpoints';
import {BASE_TEXT, WEIGHT, FONTS} from '../../../../shared/style/typography';
import {ALPHA} from '../../../../shared/style/color-utils';
import {MobileContext} from '../../../../shared/enhancers/mobile-enhancer';
import sanitizeData from './../../../../shared/utils/sanitize-data';

const ContentCard = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 81,
  ':svg': {
    width: 80,
    height: 80
  },
  [PHONE]: {
    padding: 0,
    marginTop: 63,
    marginBottom: 0
  }
});

const ContentTitle = glamorous.h4({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  marginTop: 32,
  marginBottom: 0,
  fontSize: 46,
  fontWeight: WEIGHT.ULTRA_BLACK,
  lineHeight: '60px',
  textAlign: 'left',
  color: CHARCOAL,
  [PHONE]: {
    fontSize: 24,
    fontWeight: WEIGHT.ULTRA_BLACK,
    lineHeight: '40px',
    marginBottom: 11,
    marginTop: 20
  }
});

const ContentSubTitle = glamorous.p({
  ...BASE_TEXT,
  fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
  fontSize: 30,
  fontWeight: WEIGHT.ULTRA_BLACK,
  lineHeight: '42px',
  textAlign: 'left',
  color: FOCUS_BLUE,
  margin: 0,
  [PHONE]: {
    fontSize: 24,
    lineHeight: '40px'
  }
});

const ContentDesc = glamorous.h5({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontSize: 24,
  fontWeight: WEIGHT.NORMAL,
  lineHeight: '44px',
  textAlign: 'left',
  color: CHARCOAL,
  marginTop: 32,
  [PHONE]: {
    marginTop: 20,
    fontSize: 20,
    fontWeight: WEIGHT.NORMAL,
    lineHeight: '40px',
    marginBottom: 16
  }
});

const ContentIcon = glamorous.img({
  width: 80,
  height: 80
});

const QuoteStyled = glamorous.img({
  width: 35,
  height: 35,
  [PHONE]: {
    width: 34,
    height: 'auto',
    display: 'block'
  }
});

const TestimonialContainer = glamorous.div({
  minHeight: 226,
  background: WHITE,
  boxShadow: `0px 3.95905px 65.3242px rgba(111, 111, 111, 0.15)`,
  borderRadius: 5.2,
  padding: '32px 35px',
  marginTop: 46,
  [PHONE]: {
    boxShadow: `0px 18px 34px ${ALPHA(BLACK, 0.1)}`,
    borderRadius: 4,
    padding: '33px 29px',
    width: 'auto',
    marginTop: 16
  }
});

const Testimonial = glamorous.div({
  display: 'flex',
  marginBottom: 21,
  [PHONE]: {
    display: 'block',
    marginBottom: 25
  }
});

const ContentText = glamorous.p({
  margin: 0,
  marginLeft: 27,
  fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
  fontStyle: 'normal',
  fontWeight: WEIGHT.NORMAL,
  fontSize: 24,
  lineHeight: '44px',
  [PHONE]: {
    fontSize: 18,
    lineHeight: '31px',
    textAlign: 'left',
    marginLeft: 0,
    marginTop: 19
  }
});

const ContentImage = glamorous.img({
  borderRadius: 22,
  maxWidth: 900,
  margin: '0 auto',
  marginTop: 16,
  width: '100%'
});

const AuthorInfo = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 56,
  [PHONE]: {
    marginLeft: 0
  }
});

const AuthorInfoText = glamorous.p({
  margin: 0,
  padding: 0,
  fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
  fontSize: 18,
  fontStyle: 'normal',
  fontWeight: WEIGHT.NORMAL,
  lineHeight: '44px',
  marginLeft: 2,
  [PHONE]: {
    lineHeight: '30px',
    fontSize: 16
  }
});

const List = glamorous.ul({
  marginTop: 32,
  marginBottom: 0,
  [PHONE]: {
    marginTop: 0
  }
});

const ListItem = glamorous.li({
  fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
  fontSize: 24,
  fontWeight: WEIGHT.NORMAL,
  lineHeight: '44px',
  color: CHARCOAL,
  [PHONE]: {
    fontSize: 20,
    lineHeight: '40px'
  }
});

const Avatar = glamorous.img(
  {
    borderRadius: '50%',
    marginRight: 9,
    [PHONE]: {
      width: 50,
      height: 50
    }
  },
  ({size}) => ({width: size, height: size})
);

const Caption = glamorous.p(
  {
    ...BASE_TEXT,
    fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
    fontSize: 18,
    fontWeight: WEIGHT.NORMAL,
    lineHeight: '64px',
    color: TARMAC,
    textAlign: 'center',
    margin: 0,
    padding: 0,
    marginTop: -46
  },
  ({captionStyle}) => (captionStyle ? captionStyle : {})
);

const QuoteEndIconStyled = glamorous.img({
  display: 'block',
  width: 35,
  height: 35,
  marginTop: -25,
  float: 'right',
  [PHONE]: {
    marginTop: -55,
    display: 'block'
  }
});

const FeatureCard = ({
  feature,
  key,
  customStyle = {
    testimonialStyle: {},
    authorStyle: {},
    cardStyle: {}
  }
}) => {
  const isMobile = useContext(MobileContext);
  return (
    <>
      <ContentCard key={key} cardStyle={customStyle.cardStyle}>
        <ContentIcon src={feature.icon} alt={feature.title} />
        <ContentTitle>{feature.title}</ContentTitle>
        {feature.subTitle && <ContentSubTitle>{feature.subTitle}</ContentSubTitle>}
        <ContentDesc>{feature.description}</ContentDesc>
        <List>
          {feature.points.map((point, index) => (
            <ListItem key={index}>{point}</ListItem>
          ))}
        </List>
        {!isMobile && (
          <>
            <ContentImage
              src={feature.image}
              alt={feature.title}
              style={{maxWidth: feature.maxImageSize}}
            />
            <Caption
              dangerouslySetInnerHTML={{
                __html: sanitizeData(feature.caption)
              }}
              captionStyle={feature.captionStyle}
            />
          </>
        )}
        {feature.testimonial && (
          <TestimonialContainer>
            <Testimonial testimonialStyle={customStyle.testimonialStyle}>
              <QuoteStyled
                src={'https://img.stackshare.io/fe/private/quote.png'}
                alt="Quote icon"
              />
              <ContentText>{feature.testimonial.text}</ContentText>
            </Testimonial>
            <QuoteEndIconStyled
              src={'https://img.stackshare.io/fe/landing/quote-end.svg'}
              alt="Quote end icon"
            />
            <AuthorInfo authorStyle={customStyle.authorStyle}>
              {feature.testimonial.image && <Avatar size={62} src={feature.testimonial.image} />}
              <AuthorInfoText>
                {`${feature.testimonial.name}${feature.testimonial.position}`}
              </AuthorInfoText>
            </AuthorInfo>
          </TestimonialContainer>
        )}
      </ContentCard>
    </>
  );
};

FeatureCard.propTypes = {
  customStyle: PropTypes.object,
  key: PropTypes.string,
  feature: PropTypes.object
};

export default FeatureCard;
